import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import NavigationHeader from "@/components/NavigationHeader";
import Footer from "@/components/Footer";
import DemoModal from "@/pages/Demo";
import SEO from "@/components/SEO";
import HomeAuroraBackground from "@/components/HomeAuroraBackground";
import { Mail, Phone, MapPin, Clock, Zap, Plus, Minus, MessageCircle } from "lucide-react";
import { FaFacebookF, FaLinkedinIn, FaInstagram, FaMobileAlt } from "react-icons/fa";
import { FaTiktok } from "react-icons/fa6";

const HERO_EASE = [0.16, 1, 0.3, 1];

const heroTextContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.14,
      delayChildren: 0.05,
    },
  },
};

const heroTextItem = {
  hidden: { opacity: 0, y: 56, filter: "blur(10px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: {
      duration: 0.9,
      ease: HERO_EASE,
    },
  },
};

const headerSegments = [
  { text: "Get in Touch.", gradient: "text-[#1A3A5C]" },
  {
    text: "Let's Connect.",
    gradient:
      "bg-gradient-to-r from-[#9ecc48] via-teal-400 to-blue-500 bg-clip-text text-transparent",
  },
];

// Blinking cursor component
const BlinkingCursor = () => (
  <motion.span
    className="ml-1 inline-block h-8 w-0.5 bg-[#7CB342] md:h-10"
    animate={{ opacity: [1, 0, 1] }}
    transition={{ duration: 1, repeat: Infinity, ease: "steps(2)" }}
  />
);

const Contact = () => {
  const [openFaq, setOpenFaq] = useState(0);
  const [demoModalOpen, setDemoModalOpen] = useState(false);

  const faqs = [
    {
      question: "What is Vet Assist and how can it benefit my vet clinic?",
      answer:
        "Vet Assist is a practice management software designed for vet clinics, pet hotels, and pet salon. It helps manage appointments, patient records, billing, and more. It’s like an all-in-one tool that makes running a clinic easier and more efficient.",
    },
    {
      question: "How long does it take to get started with Vet Assist?",
      answer:
        "Getting started with Vet Assist is pretty quick! Once you sign up, you can start using the basic features right away. Full setup, including data migration and training, might take a bit longer, but our team is here to help make the process smooth and fast.",
    },
    {
      question: "What features does Vet Assist offer to improve my clinic's operations?",
      answer:
        "Vet Assist offers a range of features like automated appointment reminders, electronic veterinary medical records, billing management, and even an online store option. These tools help streamline daily tasks and improve client communication, making your clinic more efficient.",
    },
    {
      question: "Do I need technical skills to use Vet Assist, or is it user-friendly?",
      answer:
        "You don’t need to be a tech expert to use Vet Assist! It’s designed to be user-friendly with a simple, intuitive interface. We also provide training and support to help you get comfortable with the system.",
    },
    {
      question: "How much does Vet Assist cost, and is it worth the investment?",
      answer:
        "The cost of Vet Assist depends on the plan you choose. We offer different packages to fit various needs and budgets. It’s definitely worth the investment because it can save you time, reduce errors, and help grow your practice by improving efficiency and client satisfaction.",
    },
    {
      question: "Can Vet Assist help my clinic attract more clients?",
      answer:
        "Yes, Vet Assist includes features like a dedicated website and online booking, which can help increase your clinic’s visibility and attract more clients. It makes it easier for pet owners to find your services and book appointments.",
    },
    {
      question: "Is Vet Assist a one-time purchase, or does it require ongoing payment?",
      answer:
        "Vet Assist is a subscription-based service, so it requires ongoing payments. This model ensures you always have access to the latest features and updates, as well as customer support. Regular updates help keep your system running smoothly and securely.",
    },
    {
      question: "How do I create certificates in Vet Assist?",
      answer:
        "Go to Certificates, select the certificate type (Referral, Travel, Veterinary, or Death), click Add, fill in Owner, Pet, and Vet details, then click Submit.",
    },
    {
      question: "How do I manage expenditures and view analytics?",
      answer:
        "Go to Finances → Expenditures, click Add Expense to log entries, or switch to Analytics to view charts and expense reports.",
    },
    {
      question: "How do I add a staff member?",
      answer:
        "Go to Clinic Operations → Staff, click Register Staff, fill in details, and click Register Staff.",
    },
    {
      question: "How do I create staff roles and permissions?",
      answer:
        "Go to Clinic Operations → Roles, click Create Role, set permissions, and click Create Role.",
    },
    {
      question: "How do I manage transactions and view sales analytics?",
      answer:
        "Go to Finances → Transactions to manage invoices, or click Analytics to view sales charts and reports.",
    },
    {
      question: "How do I create an e-waiver?",
      answer:
        "Go to Clinic Operations → E-Waivers, click Add E-Waiver, fill in details, assign services, then click Save.",
    },
    {
      question: "How do I create a customer during invoice creation?",
      answer:
        "Go to Finances → Create Invoice, turn off existing customer selection, enter details, then click Save.",
    },
    {
      question: "How do I merge invoices?",
      answer:
        "Go to Finances → Merge Invoices, select unpaid invoices, click Preview Merge, then Merge Invoices.",
    },
    {
      question: "How do I set clinic operating hours?",
      answer:
        "Go to Clinic Operations → Clinic → Business Availability, click Edit Hours, adjust schedule, then click Save.",
    },
    {
      question: "How do I manage tasks?",
      answer: "Go to Tasks, click Create to add lists or tasks, assign details, and click Create.",
    },
    {
      question: "How do I manage medical records?",
      answer:
        "Go to Medical Records, select a pet, click Create New Medical Record, then add entries like notes, diagnoses, and prescriptions.",
    },
    {
      question: "How do I create medical record templates?",
      answer: "Go to Medical Records → Templates, click Add Template, fill in details, then save.",
    },
    {
      question: "How do I print an invoice or receipt in Vet Assist?",
      answer:
        "Go to Finances → Transactions, search and open the invoice using the eye icon, then click Preview. Configure your printer in Thermal Printing Settings, click Done, and select Print Receipt to print.",
    },
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div
      className="relative w-full overflow-hidden font-sans text-slate-900"
      style={{
        background: "linear-gradient(135deg, #dbeafe 0%, #e8f4ff 20%, #f0fdf4 60%, #dcfce7 100%)",
      }}>
      <SEO page="contact" />
      <HomeAuroraBackground
        showStars={false}
        showWavingLines={true}
        className="pointer-events-none fixed inset-0 z-0 overflow-hidden"
      />
      <NavigationHeader />

      <div className="relative z-10">
        {/* Hero Section */}
        <section className="relative px-4 pt-32 pb-16 md:px-8 lg:px-16">
          <div className="relative z-10 mx-auto max-w-7xl">
            <motion.div
              variants={heroTextContainer}
              initial="hidden"
              animate="show"
              className="flex flex-col items-center text-center">
              <motion.span
                variants={heroTextItem}
                className="mb-6 inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-5 py-2 text-sm font-medium text-emerald-800 shadow-sm backdrop-blur-sm transition-colors hover:bg-emerald-500/20">
                <MessageCircle className="h-4 w-4 text-emerald-500" />
                CONTACT US
              </motion.span>
              <motion.h1
                variants={heroTextItem}
                style={{ perspective: "1000px" }}
                className="flex flex-col gap-0 text-5xl leading-[1.1] font-bold tracking-tighter md:text-7xl lg:text-[5rem]">
                {headerSegments.map((seg, i) => (
                  <div key={i} className="-mb-8 overflow-hidden pb-8">
                    <motion.span variants={heroTextItem} className={`block pb-2 ${seg.gradient}`}>
                      {seg.text}
                    </motion.span>
                  </div>
                ))}
              </motion.h1>
              <motion.p
                variants={heroTextItem}
                className="mx-auto mt-6 max-w-2xl text-xl text-[#5A7FA8] md:text-2xl">
                Need more information?
                <br />
                Our team is ready to assist you.
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Contact Info Card */}
        <section className="relative px-4 pb-16 md:px-8 lg:px-16">
          <motion.div
            className="mx-auto max-w-7xl"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}>
            <div className="group relative">
              <div className="absolute -inset-1 rounded-3xl bg-linear-to-r from-[#2546A1] via-[#7CB342] to-[#2546A1] opacity-20 blur-xl transition-opacity duration-500 group-hover:opacity-40" />

              <div className="relative rounded-3xl border border-white/10 bg-slate-900/60 p-10 shadow-2xl backdrop-blur-xl md:p-12">
                <div className="mb-8 grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-8 lg:gap-12">
                  {/* Left Column */}
                  <div className="space-y-8 lg:space-y-12">
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 }}>
                      <div className="mb-4 flex items-center gap-4 lg:mb-6">
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-linear-to-br from-[#7CB342] to-emerald-600 shadow-lg lg:h-16 lg:w-16">
                          <Phone className="h-7 w-7 text-white lg:h-8 lg:w-8" />
                        </div>
                        <h3 className="text-xl font-semibold text-[#7CB342] lg:text-2xl">
                          Contacts
                        </h3>
                      </div>
                      <div className="space-y-4 pl-0 text-base text-slate-300 sm:pl-8 md:pl-12 lg:pl-20 lg:text-lg xl:text-xl">
                        <a
                          href="tel:+639064303954"
                          className="group/link flex items-center gap-3 transition-colors hover:text-[#7CB342] lg:gap-4">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-800 transition-colors group-hover/link:bg-emerald-900/50 lg:h-10 lg:w-10">
                            <FaMobileAlt className="h-5 w-5 lg:h-6 lg:w-6" />
                          </span>
                          <span>+63 898 019 9294</span>
                        </a>
                        <a
                          href="tel:+63272750125"
                          className="group/link flex items-center gap-3 transition-colors hover:text-[#7CB342] lg:gap-4">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-800 transition-colors group-hover/link:bg-emerald-900/50 lg:h-10 lg:w-10">
                            <Phone className="h-5 w-5 lg:h-6 lg:w-6" />
                          </span>
                          <span>+63 2 7275 0125</span>
                        </a>
                        <a
                          href="mailto:customercare@vetassist.app"
                          className="group/link flex items-center gap-3 transition-colors hover:text-[#7CB342] lg:gap-4">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-800 transition-colors group-hover/link:bg-emerald-900/50 lg:h-10 lg:w-10">
                            <Mail className="h-5 w-5 lg:h-6 lg:w-6" />
                          </span>
                          <span className="break-all xl:break-normal">
                            customercare@vetassist.app
                          </span>
                        </a>
                      </div>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6 }}>
                      <div className="mb-4 flex items-center gap-4 lg:mb-6">
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-linear-to-br from-[#2546A1] to-indigo-600 shadow-lg lg:h-16 lg:w-16">
                          <Clock className="h-7 w-7 text-white lg:h-8 lg:w-8" />
                        </div>
                        <h3 className="text-xl font-semibold text-[#7CB342] lg:text-2xl">
                          Working Hours
                        </h3>
                      </div>
                      <ul className="space-y-3 pl-0 text-base text-slate-300 sm:pl-8 md:pl-12 lg:pl-20 lg:text-lg xl:text-xl">
                        <li className="flex items-start gap-3">
                          <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-green-500 xl:mt-2.5" />
                          <span className="leading-snug">MON to FRI: 9AM - 6PM</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-yellow-500 xl:mt-2.5" />
                          <span className="leading-snug">SAT: 10AM - 3PM</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-red-500 xl:mt-2.5" />
                          <span className="leading-snug">SUN: CLOSED</span>
                        </li>
                      </ul>
                    </motion.div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-8 lg:space-y-12">
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 }}>
                      <div className="mb-4 flex items-center gap-4 lg:mb-6">
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-linear-to-br from-purple-500 to-pink-600 shadow-lg lg:h-16 lg:w-16">
                          <MapPin className="h-7 w-7 text-white lg:h-8 lg:w-8" />
                        </div>
                        <h3 className="text-xl font-semibold text-[#7CB342] lg:text-2xl">
                          Address
                        </h3>
                      </div>
                      <a
                        href="https://www.google.com/maps/place/17+VATICAN+BUILDING/@14.4282883,120.988638,3a,75y,294.63h,94.78t/data=!3m7!1e1!3m5!1srxJayUh2yBGtpOMzRBQlmA!2e0!6shttps:%2F%2Fstreetviewpixels-pa.googleapis.com%2Fv1%2Fthumbnail%3Fcb_client%3Dmaps_sv.tactile%26w%3D900%26h%3D600%26pitch%3D-4.783082577780618%26panoid%3DrxJayUh2yBGtpOMzRBQlmA%26yaw%3D294.62616014500304!7i16384!8i8192!4m15!1m8!3m7!1s0x3397d1f5e2585557:0x9305f8cbb18dd00!2sUnit+302+3rd+Flr,+17+Vatican+City+Dr,+Las+Pi%C3%B1as,+1747+Metro+Manila!3b1!8m2!3d14.4284393!4d120.9885394!16s%2Fg%2F11rfddyd0z!3m5!1s0x3397d1f5efb98805:0xae81f969572a5855!8m2!3d14.4282798!4d120.9885477!16s%2Fg%2F11clwn70zg?entry=ttu&g_ep=EgoyMDI2MDMxMS4wIKXMDSoASAFQAw%3D%3D"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group/address relative flex items-start gap-2 pl-0 text-base leading-relaxed text-slate-300 transition-colors duration-300 hover:text-[#7CB342] sm:pl-8 md:pl-12 lg:pl-20 lg:text-lg xl:text-xl">
                        <MapPin className="absolute top-1 left-10 hidden h-6 w-6 text-[#7CB342] opacity-0 transition-all duration-300 group-hover/address:translate-x-2 group-hover/address:opacity-100 lg:block" />
                        <span>
                          Unit 302 3rd Flr 17 Vatican Building, <br className="hidden xl:block" />
                          Vatican City Drive, <br className="hidden xl:block" />
                          Talon Dos, BF Resort Village, <br className="hidden xl:block" />
                          Las Piñas, 1746 Metro Manila
                        </span>
                      </a>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6 }}>
                      <div className="mb-4 flex items-center gap-4 lg:mb-6">
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-linear-to-br from-cyan-500 to-blue-600 shadow-lg lg:h-16 lg:w-16">
                          <MessageCircle className="h-7 w-7 text-white lg:h-8 lg:w-8" />
                        </div>
                        <h3 className="text-xl font-semibold text-[#7CB342] lg:text-2xl">
                          Follow Us
                        </h3>
                      </div>
                      <div className="flex flex-wrap gap-3 pl-0 sm:pl-8 md:pl-12 lg:gap-4 lg:pl-20">
                        <motion.a
                          href="https://facebook.com/VetAssistApp"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-slate-800 text-slate-300 transition-all duration-300 hover:border-[#2546A1] hover:bg-[#2546A1] hover:text-white lg:h-14 lg:w-14"
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          whileTap={{ scale: 0.95 }}>
                          <FaFacebookF className="h-5 w-5 lg:h-6 lg:w-6" />
                        </motion.a>
                        <motion.a
                          href="https://www.linkedin.com/company/vet-assist-app/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-slate-800 text-slate-300 transition-all duration-300 hover:border-[#2546A1] hover:bg-[#2546A1] hover:text-white lg:h-14 lg:w-14"
                          whileHover={{ scale: 1.1, rotate: -5 }}
                          whileTap={{ scale: 0.95 }}>
                          <FaLinkedinIn className="h-5 w-5 lg:h-6 lg:w-6" />
                        </motion.a>
                        <motion.a
                          href="https://www.instagram.com/vet_assist_ph/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-slate-800 text-slate-300 transition-all duration-300 hover:border-transparent hover:bg-linear-to-br hover:from-purple-500 hover:to-pink-500 hover:text-white lg:h-14 lg:w-14"
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          whileTap={{ scale: 0.95 }}>
                          <FaInstagram className="h-5 w-5 lg:h-6 lg:w-6" />
                        </motion.a>
                        <motion.a
                          href="https://www.tiktok.com/@vetassistapp?lang=en"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-slate-800 text-slate-300 transition-all duration-300 hover:border-slate-300 hover:bg-slate-700 hover:text-white lg:h-14 lg:w-14"
                          whileHover={{ scale: 1.1, rotate: -5 }}
                          whileTap={{ scale: 0.95 }}>
                          <FaTiktok className="h-5 w-5 lg:h-6 lg:w-6" />
                        </motion.a>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* FAQ Section */}
        <section className="relative px-4 pb-12 md:px-8 lg:px-16">
          <motion.div
            className="mx-auto max-w-6xl"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}>
            <div className="mb-10 text-center">
              <motion.div
                className="mb-6 inline-flex items-center gap-2 rounded-full border border-yellow-500/20 bg-yellow-500/10 px-5 py-2.5 backdrop-blur-sm"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}>
                <Zap className="h-6 w-6 text-yellow-500" />
                <span className="text-lg font-medium text-yellow-800">F.A.Q's</span>
              </motion.div>
              <h2 className="text-4xl font-bold text-[#1A3A5C] md:text-5xl lg:text-6xl">
                Have Questions? <span className="text-[#7CB342]">Start Here!</span>
              </h2>
            </div>

            <div className="space-y-5">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="group">
                  <div
                    className={`relative overflow-hidden rounded-[2rem] border transition-all duration-300 ${openFaq === index ? "border-[#7CB342] bg-slate-800/80 shadow-[0_0_20px_rgba(124,179,66,0.2)] backdrop-blur-md" : "border-white/10 bg-slate-900/40 backdrop-blur-md hover:border-white/20"}`}>
                    <button
                      onClick={() => toggleFaq(index)}
                      className="flex w-full cursor-pointer items-center justify-between p-8 text-left">
                      <span
                        className={`pr-8 text-xl font-bold transition-colors md:text-2xl ${openFaq === index ? "text-[#7CB342]" : "text-slate-200"}`}>
                        {faq.question}
                      </span>
                      <div
                        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition-all duration-300 ${openFaq === index ? "rotate-180 bg-[#7CB342]" : "bg-slate-800 group-hover:bg-[#7CB342]/20"}`}>
                        {openFaq === index ? (
                          <Minus className="h-6 w-6 text-white" />
                        ) : (
                          <Plus className="h-6 w-6 text-slate-400 group-hover:text-[#7CB342]" />
                        )}
                      </div>
                    </button>
                    <AnimatePresence>
                      {openFaq === index && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          className="overflow-hidden">
                          <div className="px-8 pt-0 pb-8">
                            <p className="border-t border-white/10 pt-6 text-lg leading-relaxed font-medium text-slate-300 md:text-xl">
                              {faq.answer}
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* CTA Section */}
        <section className="relative px-4 pb-12 md:px-8 lg:px-16">
          <motion.div
            className="mx-auto max-w-7xl"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}>
            <div className="group relative">
              <div className="absolute -inset-1 rounded-3xl bg-linear-to-r from-[#2546A1] via-[#7CB342] to-[#2546A1] opacity-30 blur-xl transition-opacity duration-500 group-hover:opacity-50" />
              <div className="relative overflow-hidden rounded-3xl bg-linear-to-br from-[#2546A1]/90 to-[#1d3782]/90 p-12 text-center backdrop-blur-xl md:p-16">
                <div className="pointer-events-none absolute inset-0 opacity-10">
                  <div
                    className="absolute inset-0"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}
                  />
                </div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}>
                  <h3 className="mb-8 text-4xl font-bold text-white md:text-5xl lg:text-6xl">
                    Ready to Transform Your Clinic?
                  </h3>
                  <p className="mx-auto mb-12 max-w-2xl text-2xl text-blue-100">
                    Join hundreds of veterinary practices using Vet Assist to streamline their
                    operations.
                  </p>
                  <div className="flex justify-center">
                    <motion.button
                      onClick={() => setDemoModalOpen(true)}
                      className="cursor-pointer rounded-xl bg-[#7CB342] px-10 py-4 text-xl font-semibold text-white shadow-lg transition-colors hover:bg-[#689F38]"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}>
                      Schedule a Demo
                    </motion.button>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </section>

        <Footer />
      </div>

      <DemoModal open={demoModalOpen} onOpenChange={setDemoModalOpen} />
    </div>
  );
};

export default Contact;
